import { useState, useRef, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAppStore } from "@/lib/store";
import { LogOut, User, Wallet, ChevronDown } from "lucide-react";

export function AvatarDisplay() {
  const { user, isAuthenticated, logout } = useAppStore();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isAuthenticated || !user || !user.fullName) return null;

  const initials = user.fullName
    .split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarUrl = user.avatarFileId
    ? `/api/avatar/${encodeURIComponent(user.avatarFileId)}`
    : null;

  const handleLogout = () => {
    setOpen(false);
    logout();
  };

  const handleNavigate = (to: string) => {
    setOpen(false);
    navigate({ to });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center gap-1 outline-none"
        aria-label="Account menu"
        aria-expanded={open}
      >
        <AvatarInner avatarUrl={avatarUrl} initials={initials} fullName={user.fullName} />
        <ChevronDown
          className="h-3 w-3 text-white/50 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      <div
        className="absolute right-0 mt-2 w-44 origin-top-right rounded-xl border border-blue-200 bg-white shadow-xl z-50 transition-all duration-200 ease-out"
        style={{
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          opacity: open ? 1 : 0,
          scale: open ? 1 : 0.9,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div className="py-1.5">
          <button
            type="button"
            onClick={() => handleNavigate("/profile")}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700"
          >
            <User className="h-4 w-4" />
            Profile
          </button>
          <button
            type="button"
            onClick={() => handleNavigate("/wallet")}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700"
          >
            <Wallet className="h-4 w-4" />
            Wallet
          </button>
          <div className="my-1 h-px bg-slate-200" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

function AvatarInner({
  avatarUrl,
  initials,
  fullName,
}: {
  avatarUrl: string | null;
  initials: string;
  fullName: string;
}) {
  const [imgError, setImgError] = useState(false);

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={fullName}
        className="h-10 w-10 rounded-full object-cover border-2 border-white/30 shadow-lg"
        loading="lazy"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-linear-to-br from-blue-700 to-indigo-700 text-xs font-bold text-white border-2 border-white/30 shadow-lg">
      {initials || "U"}
    </div>
  );
}
